import { Component, effect, OnInit, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, NgIf } from '@angular/common';
import * as _ from 'lodash';
import { SettingsService } from 'src/app/shared/services/settings';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';

// Declare Helpers globally
declare const Helpers: any;

@Component({
  selector: 'app-litmus-action-rules',
  templateUrl: './litmus-action-rules.component.html',
  styleUrls: ['./litmus-action-rules.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, LtmsLoaderComponent]
})
export class LitmusActionRulesComponent implements OnInit, OnChanges, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);
  configurationLoaded: boolean = false;

  rulesForm!: FormGroup;
  isSavingSection: 'status' | 'category' | 'field' | null = null;
  brandDetails: any = {};
  isCancellingSection: 'status' | 'category' | 'field' | null = null;
  isStatusSaveEnabled = false;
  isCategorySaveEnabled = false;
  isFieldSaveEnabled = false;
  isSaveEnabled = false;
  originalConfiguration: any = {};
  private initializedBrandId: string | null = null;
  private defaultActionSummaryColorConfig:any= {
    "widget_settings": {
        "action_summary": {
            "count": {
                "green_bucket": {
                    "label": "Solved",
                    "color": "#10a754"
                },
                "red_bucket": {
                    "label": "Unsolved", 
                    "color": "#ed2424"
                },
            },
            "ageing": {
                "green_bucket": {
                    "label": "Unsolved Within Target TAT",
                    "color": "#10a754"
                },
                "red_bucket": {
                    "label": "Unsolved Outside Of Target TAT",
                    "color": "#69a8fc"
                },
            },
            "tat": {
                "green_bucket": {
                    "label": "Solved Within Target TAT",
                    "color": "#10a754"
                },
                "red_bucket": {
                    "label": "Solved Outside of Target TAT",
                    "color": "#fab416"
                },
            }
        }
    }
}

  constructor(
    private fb: FormBuilder,
    private stateService: AppStateService,
    private _toast: ToastService,
    private _settingsService: SettingsService,
    private accountsService: AccountsService,
    private helpersService: HelpersService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        if (_.isEmpty(brandDetail)) {
          return;
        }
        if (this.rulesForm && this.initializedBrandId === brandDetail._id) {
          this.brandDetails = brandDetail;
          this.applyBrandDetailsToForm();
          return;
        }
        this.brandDetails = brandDetail;
        this.initializedBrandId = brandDetail._id;
        this.configurationLoaded = true;
        this.initializeForm();
      }
    })
  }

  ngOnInit(): void {
    if (!_.isEmpty(this.brandDetails)) {
      this.initializeForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly']) {
      this.updateFormReadonlyState();
    }
  }

  private updateFormReadonlyState(): void {
    if (!this.rulesForm) {
      return;
    }
    if (this.readonly) {
      this.rulesForm.disable({ emitEvent: false });
    } else {
      this.rulesForm.enable({ emitEvent: false });
    }
  }

  private initializeForm(): void {
    this.rulesForm = this.fb.group({
      // Status Setup - using actual brand details structure
      enableStatusSequencing: [this.brandDetails?.action_management_configuration?.visibility_rules?.status_list?.sequence?.enabled || false],
      enableStatusHierarchyAccess: [this.brandDetails?.action_management_configuration?.visibility_rules?.status_list?.hierarchy?.enabled || false],
      enableStatusParameterAccess: [this.brandDetails?.action_management_configuration?.visibility_rules?.status_list?.custom?.enabled || false],
      
      // Category Setup
      enableCategoryHierarchyAccess: [this.brandDetails?.action_management_configuration?.visibility_rules?.category_list?.hierarchy?.enabled || false],
      enableCategoryParameterAccess: [this.brandDetails?.action_management_configuration?.visibility_rules?.category_list?.custom?.enabled || false],
      
      // Field Setup
      enableFieldHierarchyAccess: [this.brandDetails?.action_management_configuration?.visibility_rules?.field_definitions?.hierarchy?.enabled || false],
      enableFieldParameterAccess: [this.brandDetails?.action_management_configuration?.visibility_rules?.field_definitions?.custom?.enabled || false]
    });

    // Listen to form changes with debounce and cleanup
    this.rulesForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.onFormChange(value);
      });

    this.updateFormReadonlyState();
    this.updateSectionSaveState();
  }

  private applyBrandDetailsToForm(): void {
    if (!this.rulesForm) {
      return;
    }
    if (this.isSaveEnabled) {
      this.updateSectionSaveState();
      return;
    }
    this.rulesForm.patchValue(this.getSavedSnapshot(), { emitEvent: false });
    this.updateSectionSaveState();
  }

  onFormChange(_value: any): void {
    this.updateSectionSaveState();
  }

  onCheckboxChange(_field: string, _value: boolean): void {
    this.updateSectionSaveState();
  }

  private getSavedSnapshot(): any {
    const visibilityRules = this.brandDetails?.action_management_configuration?.visibility_rules;
    return {
      enableStatusSequencing: !!visibilityRules?.status_list?.sequence?.enabled,
      enableStatusHierarchyAccess: !!visibilityRules?.status_list?.hierarchy?.enabled,
      enableStatusParameterAccess: !!visibilityRules?.status_list?.custom?.enabled,
      enableCategoryHierarchyAccess: !!visibilityRules?.category_list?.hierarchy?.enabled,
      enableCategoryParameterAccess: !!visibilityRules?.category_list?.custom?.enabled,
      enableFieldHierarchyAccess: !!visibilityRules?.field_definitions?.hierarchy?.enabled,
      enableFieldParameterAccess: !!visibilityRules?.field_definitions?.custom?.enabled
    };
  }

  private updateSectionSaveState(): void {
    this.isStatusSaveEnabled = this.hasSectionChanged('status');
    this.isCategorySaveEnabled = this.hasSectionChanged('category');
    this.isFieldSaveEnabled = this.hasSectionChanged('field');
    this.isSaveEnabled = this.isStatusSaveEnabled || this.isCategorySaveEnabled || this.isFieldSaveEnabled;
  }

  private getSectionFields(section: 'status' | 'category' | 'field'): string[] {
    if (section === 'status') {
      return ['enableStatusSequencing', 'enableStatusHierarchyAccess', 'enableStatusParameterAccess'];
    }
    if (section === 'category') {
      return ['enableCategoryHierarchyAccess', 'enableCategoryParameterAccess'];
    }
    return ['enableFieldHierarchyAccess', 'enableFieldParameterAccess'];
  }

  private hasSectionChanged(section: 'status' | 'category' | 'field'): boolean {
    if (!this.rulesForm || !this.brandDetails) return false;
    const currentValues = this.rulesForm.getRawValue();
    const saved = this.getSavedSnapshot();
    return this.getSectionFields(section).some((field) => !!currentValues[field] !== !!saved[field]);
  }

  private buildMergedFormValues(section: 'status' | 'category' | 'field'): any {
    const currentValues = this.rulesForm.getRawValue();
    const saved = this.getSavedSnapshot();
    const merged = { ...saved };
    this.getSectionFields(section).forEach((field) => {
      merged[field] = currentValues[field] || false;
    });
    return merged;
  }

  private buildRuleSettingConfig(formValues: any): any {
    let colorConfig = _.get(this.brandDetails, "action_management_configuration.widget_settings.action_summary");
    let widget_settings:any = _.get(this.brandDetails, "action_management_configuration.widget_settings");
    if (colorConfig && !_.isEmpty(colorConfig) && !(colorConfig && !_.isEmpty(colorConfig))) {
      widget_settings = _.cloneDeep(this.defaultActionSummaryColorConfig.widget_settings);
    }
    let action_management_configuration:any = _.get(this.brandDetails, "action_management_configuration",{});
    action_management_configuration.visibility_rules = {
      status_list: {
        sequence: {
          enabled: formValues.enableStatusSequencing || false,
          type: "visibility"
        },
        hierarchy: {
          enabled: formValues.enableStatusHierarchyAccess || false,
          is_strict: formValues.enableStatusHierarchyAccess || false
        },
        custom: {
          enabled: formValues.enableStatusParameterAccess || false
        }
      },
      category_list: {
        hierarchy: {
          enabled: formValues.enableCategoryHierarchyAccess || false,
          strict_access: formValues.enableCategoryHierarchyAccess || false
        },
        custom: {
          enabled: formValues.enableCategoryParameterAccess || false
        }
      },
      field_definitions: {
        hierarchy: {
          enabled: formValues.enableFieldHierarchyAccess || false,
          strict_access: formValues.enableFieldHierarchyAccess || false
        },
        custom: {
          enabled: formValues.enableFieldParameterAccess || false
        }
      },
      accordions_list: {
        hierarchy: {
          enabled: formValues.enableFieldHierarchyAccess || false,
          strict_access: formValues.enableFieldHierarchyAccess || false
        },
        custom: {
          enabled: formValues.enableFieldParameterAccess || false
        }
      }
    }
    action_management_configuration.widget_settings = widget_settings;
    return {action_management_configuration: action_management_configuration};
  }

  saveConfiguration(section: 'status' | 'category' | 'field') {
    if (this.readonly) {
      Helpers.smallAlertBox('Access denied', "You do not have write permission", "info", 1500);
      return;
    }
    if (this.isSavingSection) return;

    this.isSavingSection = section;
    const formValues = this.buildMergedFormValues(section);
    const ruleSettingConfig = this.buildRuleSettingConfig(formValues);

    this._settingsService.updateBrandDashboardConfiguration(this.brandDetails._id, ruleSettingConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.code == 0) {
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Success', 'Action configuration has been updated successfully.', 'success', 3500);
            }
            this.afterUpdateBrandConfig();
          } else if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Error while updating Rules settings.', 'error', 3500);
          }
          this.isSavingSection = null;
          this.updateSectionSaveState();
        },
        error: (error: any) => {
          console.error('Error while updating Rules settings:', error);
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Error while updating Rules settings.', 'error', 3500);
          }
          this.isSavingSection = null;
        }
      });
  }

  afterUpdateBrandConfig() {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
        this.updateSectionSaveState();
      }
    });
  }

  cancelConfiguration(section: 'status' | 'category' | 'field') {
    if (this.readonly) {
      Helpers.smallAlertBox('Access denied', "You do not have write permission", "info", 1500);
      return;
    }
    if (this.isCancellingSection) return;

    this.isCancellingSection = section;
    const saved = this.getSavedSnapshot();
    const patch: any = {};
    this.getSectionFields(section).forEach((field) => {
      patch[field] = saved[field];
    });
    this.rulesForm.patchValue(patch);
    this.updateSectionSaveState();
    this.isCancellingSection = null;
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: "You have reordered the rows",
            content: "To save your changes, please use the <b>Save</b> button at the bottom of the card. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.",
            buttons: [
              { text: "Cancel", type: "secondary", key: "Cancel" },
              { text: "Discard Changes", type: "primary", key: "Discard" }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === "Discard") {
                this.isStatusSaveEnabled = false;
                this.isCategorySaveEnabled = false;
                this.isFieldSaveEnabled = false;
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        // Fallback if Helpers is not available
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isStatusSaveEnabled = false;
          this.isCategorySaveEnabled = false;
          this.isFieldSaveEnabled = false;
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}
